import { useMemo, useState } from 'react';

import { useRights, useRightsAvailability, useShows } from '../../api/hooks';
import type { AvailabilityFilters, RightStatus, RightType } from '../../api/types';
import {
  Badge,
  Button,
  DataTable,
  DateInput,
  FormField,
  Select,
} from '../../components/DesignSystem';
import type { DataTableColumn } from '../../components/DesignSystem';
import { formatDate } from '../../lib/format';

interface DraftFilters {
  showId: string;
  asOf: string;
  territory: string;
  mediaType: string;
  rightType: string;
  status: string;
  category: string;
  territoryTier: string;
  platform: string;
  device: string;
  tier: string;
  language: string;
  version: string;
}

const EMPTY_FILTERS: DraftFilters = {
  showId: '',
  asOf: '',
  territory: '',
  mediaType: '',
  rightType: '',
  status: '',
  category: '',
  territoryTier: '',
  platform: '',
  device: '',
  tier: '',
  language: '',
  version: '',
};

/** Distinct non-null values of one right field, for filter dropdowns. */
function distinct(values: (string | null)[]): string[] {
  return Array.from(new Set(values.filter((value): value is string => value !== null))).sort();
}

/** Availability Search — stack any combination of dimensions. */
export function AvailabilityTab() {
  const shows = useShows({ limit: 100 });
  const rights = useRights({ limit: 100 });

  const [draft, setDraft] = useState<DraftFilters>(EMPTY_FILTERS);
  const [submitted, setSubmitted] = useState<AvailabilityFilters | null>(null);

  const results = useRightsAvailability(submitted);

  const rightRows = useMemo(() => rights.data?.data ?? [], [rights.data]);

  const options = useMemo(
    () => ({
      territory: distinct(rightRows.map((row) => row.territory)),
      mediaType: distinct(rightRows.map((row) => row.mediaType)),
      category: distinct(rightRows.map((row) => row.category)),
      territoryTier: distinct(rightRows.map((row) => row.territoryTier)),
      platform: distinct(rightRows.map((row) => row.platform)),
      device: distinct(rightRows.map((row) => row.device)),
      tier: distinct(rightRows.map((row) => row.tier)),
      language: distinct(rightRows.map((row) => row.language)),
      version: distinct(rightRows.map((row) => row.version)),
    }),
    [rightRows],
  );

  const set = (patch: Partial<DraftFilters>) => setDraft((current) => ({ ...current, ...patch }));

  function handleSearch() {
    setSubmitted({
      showId: draft.showId || undefined,
      asOf: draft.asOf || undefined,
      territory: draft.territory || undefined,
      mediaType: draft.mediaType || undefined,
      rightType: (draft.rightType || undefined) as RightType | undefined,
      status: (draft.status || undefined) as RightStatus | undefined,
      category: draft.category || undefined,
      territoryTier: draft.territoryTier || undefined,
      platform: draft.platform || undefined,
      device: draft.device || undefined,
      tier: draft.tier || undefined,
      language: draft.language || undefined,
      version: draft.version || undefined,
    });
  }

  const dimensionSelect = (key: keyof DraftFilters, label: string, values: string[]) => (
    <FormField htmlFor={`avail-${key}`} label={label}>
      <Select
        id={`avail-${key}`}
        onChange={(event) => set({ [key]: event.target.value } as Partial<DraftFilters>)}
        value={draft[key]}
      >
        <option value="">Any</option>
        {values.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </Select>
    </FormField>
  );

  const columns: DataTableColumn<NonNullable<typeof results.data>[number]>[] = [
    { key: 'showName', header: 'Title' },
    { key: 'trackId', header: 'Track ID' },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge
          tone={
            row.status === 'active' ? 'teal' : row.status === 'expiring' ? 'neutral' : 'primary'
          }
        >
          {row.status}
        </Badge>
      ),
    },
    { key: 'territories', header: 'Territories', render: (row) => row.territories.join(', ') },
    {
      key: 'licenseWindow',
      header: 'License Window',
      render: (row) =>
        row.licenseWindow
          ? `${formatDate(row.licenseWindow.start)} → ${formatDate(row.licenseWindow.end)}`
          : '—',
    },
    { key: 'rightsCount', header: 'Rights', align: 'right' },
  ];

  return (
    <>
      <section className="rfp-card">
        <div className="rfp-card__head">
          <div>
            <h2 className="rfp-card__title">Availability Search</h2>
            <p className="rfp-card__caption">
              Stack any combination of dimensions to ask: where, how, when, and in what form can
              this title be shown?
            </p>
          </div>
        </div>

        <div className="rfp-filter-grid">
          <FormField htmlFor="avail-show" label="Show / Title">
            <Select
              id="avail-show"
              onChange={(event) => set({ showId: event.target.value })}
              value={draft.showId}
            >
              <option value="">Any show</option>
              {(shows.data?.data ?? []).map((show) => (
                <option key={show.id} value={show.id}>
                  {show.name}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField htmlFor="avail-asOf" label="As of Date">
            <DateInput
              id="avail-asOf"
              onChange={(event) => set({ asOf: event.target.value })}
              value={draft.asOf}
            />
          </FormField>

          {dimensionSelect('territory', 'Territory', options.territory)}
          {dimensionSelect('mediaType', 'Rights Type / Media', options.mediaType)}

          <FormField htmlFor="avail-rightType" label="Right Type">
            <Select
              id="avail-rightType"
              onChange={(event) => set({ rightType: event.target.value })}
              value={draft.rightType}
            >
              <option value="">Any</option>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </Select>
          </FormField>

          <FormField htmlFor="avail-status" label="Status">
            <Select
              id="avail-status"
              onChange={(event) => set({ status: event.target.value })}
              value={draft.status}
            >
              <option value="">Any</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring</option>
              <option value="expired">Expired</option>
              <option value="holdback">Holdback</option>
            </Select>
          </FormField>

          {dimensionSelect('category', 'Category', options.category)}
          {dimensionSelect('territoryTier', 'Territory Tier', options.territoryTier)}
          {dimensionSelect('platform', 'Platform', options.platform)}
          {dimensionSelect('device', 'Device', options.device)}
          {dimensionSelect('tier', 'Tier', options.tier)}
          {dimensionSelect('language', 'Language', options.language)}
          {dimensionSelect('version', 'Version', options.version)}
        </div>

        <div className="rfp-wizard-actions">
          <Button onClick={() => setDraft(EMPTY_FILTERS)} type="button" variant="secondary">
            Clear
          </Button>
          <Button onClick={handleSearch} type="button">
            Search Availability
          </Button>
        </div>
      </section>

      {submitted !== null && (
        <section className="rfp-card">
          <div className="rfp-card__head">
            <h2 className="rfp-card__title">
              Results {results.data ? `(${results.data.length})` : ''}
            </h2>
          </div>
          {results.isLoading ? (
            <p className="rfp-loading">Searching…</p>
          ) : results.isError ? (
            <p className="rfp-inline-error" role="alert">
              Availability search failed — please retry.
            </p>
          ) : (
            <DataTable
              columns={columns}
              emptyMessage="Nothing matches this combination of dimensions"
              rowKey={(row) => `${row.showId}-${row.trackId ?? 'all'}`}
              rows={results.data ?? []}
            />
          )}
        </section>
      )}
    </>
  );
}
